import { FormControl, FormErrorMessage, FormLabel, Input, Select, Text } from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { SelectionCriterionDto, SelectionCriterionType } from "../../../autogen/bff-api";
import { TipTap } from "../../input/TipTap/TipTap";
import { SELECTION_CRITERION_TYPE_OPTIONS } from "../../procurement/publicProcurementOptions";
import { FormOptionRow, FormSectionBand } from "./FormSectionBand";

// t('Title') t('Requirement title') t('Title is required') t('Description') t('optional')
// t('Category') t('Choose a category') t('Category is required')
// t('Options')
// t('Requires documentation') t('The supplier must upload an attachment')

export interface QualificationFormValues {
  type: SelectionCriterionType | "";
  name: string;
  description: string;
  requiresEvidence: boolean;
}

interface Props {
  /** Lets a button outside the form submit it, so the same fields work in a drawer, a modal and a tab. */
  formId: string;
  defaultValues?: QualificationFormValues;
  onSubmit: (values: SelectionCriterionDto) => Promise<void> | void;
  /** Rendered inside the form, under the fields — where the "save to the library" choice belongs. */
  children?: React.ReactNode;
}

const emptyDefaults: QualificationFormValues = {
  type: "",
  name: "",
  description: "",
  requiresEvidence: false,
};

/**
 * The one definition of what a qualification requirement asks for, shared by the wizard's edit drawer,
 * the requirement library's modal and the "write a new one" tab.
 */
export const QualificationRequirementFields = ({ formId, defaultValues, onSubmit, children }: Props) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<QualificationFormValues>({ defaultValues: defaultValues ?? emptyDefaults });

  const submitHandler = handleSubmit(async (values) => {
    if (!values.type) return;
    await onSubmit({
      type: values.type,
      name: values.name,
      description: values.description,
      requiresEvidence: values.requiresEvidence,
    });
  });

  return (
    <form id={formId} data-testid="qualification-requirement-form" onSubmit={submitHandler}>
      <FormControl mb="6" isRequired isInvalid={errors.name !== undefined}>
        <FormLabel>{t("Title")}</FormLabel>
        <Input
          autoFocus
          autoComplete="off"
          data-1p-ignore
          placeholder={t("Requirement title") ?? ""}
          {...register("name", { required: t("Title is required") ?? "" })}
        />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mb="6">
        <FormLabel>
          {t("Description")}{" "}
          <Text as="span" color="smMuted" fontWeight="normal">
            {t("optional")}
          </Text>
        </FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TipTap
              content={value}
              onUpdate={onChange}
              editable={true}
              toolbar={{
                underline: false,
                headings: false,
                colors: false,
                link: false,
                table: false,
                image: false,
                template: false,
              }}
              debounceWait={0}
            />
          )}
        />
      </FormControl>

      {/* A dropdown, not chips: the category list is expected to grow, and chips stop scaling once
          they wrap onto a third row. */}
      <FormControl mb="6" isRequired isInvalid={errors.type !== undefined}>
          <FormLabel>{t("Category")}</FormLabel>
          <Select
            {...register("type", { required: t("Category is required") ?? "" })}
            borderColor={errors.type ? "smError" : "smInputBorder"}
          >
            <option value="">{t("Choose a category")}</option>
            {SELECTION_CRITERION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.value)}
              </option>
            ))}
          </Select>
        <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
      </FormControl>

      <FormSectionBand>{t("Options")}</FormSectionBand>

      <Controller
        name="requiresEvidence"
        control={control}
        render={({ field: { value, onChange } }) => (
          <FormOptionRow
            isChecked={value}
            onChange={onChange}
            title={t("Requires documentation")}
            hint={t("The supplier must upload an attachment")}
          />
        )}
      />

      {children}
    </form>
  );
};
